import { createContext, useContext, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { bookConsultation } from "../lib/api";
import { SERVICES } from "../data";

const Ctx = createContext(null);
export const useConsultation = () => useContext(Ctx);

export function ConsultationProvider({ children }) {
  const [open, setOpen] = useState(false);
  const openModal = () => setOpen(true);
  const closeModal = () => setOpen(false);

  return (
    <Ctx.Provider value={{ openModal, closeModal }}>
      {children}
      <ConsultationModal open={open} onClose={closeModal} />
    </Ctx.Provider>
  );
}

function ConsultationModal({ open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", service: SERVICES[0].title, preferred_date: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please fill in your name and email.");
      return;
    }
    setLoading(true);
    try {
      await bookConsultation(form);
      setDone(true);
      toast.success("Consultation request sent. We'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setDone(false);
      setForm({ name: "", email: "", service: SERVICES[0].title, preferred_date: "", notes: "" });
    }, 300);
  };

  const inputCls =
    "w-full rounded-xl bg-[#0a0a0a] border border-white/10 px-4 py-3 text-white placeholder-slate-500 outline-none transition-colors focus:border-blue-500 focus:ring-1 focus:ring-blue-500";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[90] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-md"
            onClick={handleClose}
            data-testid="consultation-overlay"
          />
          <motion.div
            className="relative z-10 w-full max-w-lg rounded-3xl border border-white/10 bg-[#0a0a0a]/95 p-8 shadow-[0_0_64px_rgba(37,99,235,0.15)]"
            initial={{ scale: 0.94, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.94, y: 20, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            data-testid="consultation-modal"
          >
            <button
              onClick={handleClose}
              className="absolute right-5 top-5 text-slate-400 transition-colors hover:text-white"
              data-testid="consultation-close-button"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

            {done ? (
              <div className="flex flex-col items-center py-8 text-center">
                <CheckCircle2 className="mb-4 h-14 w-14 text-blue-500" />
                <h3 className="font-display text-2xl font-semibold">Request received</h3>
                <p className="mt-2 text-slate-400">
                  Thank you, {form.name.split(" ")[0] || "there"}. Our team will reach out to confirm your session.
                </p>
                <button
                  onClick={handleClose}
                  className="mt-6 rounded-full bg-blue-600 px-8 py-3 font-medium text-white transition-colors hover:bg-blue-700"
                  data-testid="consultation-done-button"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-500">Book a session</p>
                <h3 className="mt-2 font-display text-3xl font-semibold tracking-tight">Book Consultation</h3>
                <p className="mt-1 text-sm text-slate-400">Tell us a little about your needs and we&rsquo;ll take it from there.</p>
                <form onSubmit={submit} className="mt-6 space-y-4" data-testid="consultation-form">
                  <input className={inputCls} placeholder="Full name" value={form.name} onChange={update("name")} data-testid="consultation-name-input" />
                  <input className={inputCls} type="email" placeholder="Email address" value={form.email} onChange={update("email")} data-testid="consultation-email-input" />
                  <select className={inputCls} value={form.service} onChange={update("service")} data-testid="consultation-service-select">
                    {SERVICES.map((s) => (
                      <option key={s.key} value={s.title} className="bg-[#0a0a0a]">
                        {s.title}
                      </option>
                    ))}
                  </select>
                  <input className={inputCls} placeholder="Preferred date (optional)" value={form.preferred_date} onChange={update("preferred_date")} data-testid="consultation-date-input" />
                  <textarea className={inputCls} rows={3} placeholder="Anything you'd like us to know? (optional)" value={form.notes} onChange={update("notes")} data-testid="consultation-notes-input" />
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-blue-600 py-4 font-medium text-white shadow-[0_0_24px_rgba(37,99,235,0.4)] transition-all duration-300 hover:bg-blue-700 hover:shadow-[0_0_32px_rgba(37,99,235,0.6)] disabled:opacity-60"
                    data-testid="consultation-submit-button"
                  >
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Request Consultation"}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
